import React from 'react';
import { useTranslation } from 'react-i18next'; 
import './CourseTimelineOverview.css';
import GradientButton from '../ui/GradientButton/GradientButton';
import speakerIcon from './../../assets/icons/ui/speaker.svg';
import infoIcon from './../../assets/icons/ui/info.svg';
import VerifiedIcon from './../../assets/icons/ui/verified.svg'; 
import ImportantIcon from './../../assets/icons/ui/important.svg';

interface Stats {
  lessons: number;
  lectures: number;
  hours: number;
  certificate: boolean;
}

interface CourseTimelineOverviewProps {
  date: string;
  startDate: string; // ✅ Added start date for filtering
  title: string;
  description: string;
  image: string;
  stats: Stats;
  mastersName: string;
  programPrice: string;
  buttonText: string;
  onButtonClick: () => void;
  whatsIncluded: string[]; 
} 

const CourseTimelineOverview: React.FC<CourseTimelineOverviewProps> = ({
  date,
  startDate, // ✅ New startDate prop
  title,
  description,
  image,
  stats,
  buttonText,
  onButtonClick,
  programPrice,
  mastersName,
  whatsIncluded
}) => {
  const { t } = useTranslation();

  // ✅ Get today's date and normalize
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ✅ Convert program start date to a Date object
  const programStartDate = new Date(startDate);
  programStartDate.setHours(0, 0, 0, 0);

  // ✅ Check if the program has already started
  const isProgramStarted = today > programStartDate;

  return (
    <div className="course-overview-container">
      {/* Image Section */}
      <div className="program-overview-image">
        <img src={image} alt={t("course_image_alt")} />
      </div>
 
      {/* Content Section */}
      <div className="course-overview-content">
        <p className="course-date">{t("course_date")}: {date}</p>
        <span className='speaker-name-title'>
          <img src={speakerIcon} alt="Speaker" className="location-icon" />
          <span style={{fontWeight:'400'}}>{t("course_speaker")}:</span> {mastersName}
        </span>
        <h1 className="program-title">{title}</h1>
        <p className="course-description">{description}</p>
        
        {/* ✅ Show either price & button OR a message if the program has started */}
        {isProgramStarted ? (
         <div>
           <p className="program-started-message" style={{ display: 'flex' }}>
           <img src={ImportantIcon} alt="Verified" className="location-icon" /> {t("program_already_started")} {/* Translatable message */}
          </p>
          
          </div>
          
        ) : (
          <div className='event-price-container'>
            <div>
              <p className="event-price-description">{t("course_price_label")}</p>
              <h2 className="program-price">{programPrice}</h2>

              {/* What's Included Section */}
              <div className="tooltip-container">
                <p className='include-in-the-ticket' style={{ display: 'flex' }}>
                  <img src={infoIcon} alt="Info" className="speaker-icon" />
                  {t("course_whats_included")}
                </p>
                <div className="tooltip-text">
                  <div style={{ display: 'grid' }}>
                    <ul className='benefits-list-event'>
                      {whatsIncluded.map((benefit, index) => (
                        <li key={index} className='event-benefit'>
                          <img src={VerifiedIcon} alt="Verified" className="location-icon" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <GradientButton onClick={onButtonClick}>{buttonText}</GradientButton>
          </div>
        )}
      </div>
 
      {/* Stats Section */}
      <div className="program-overview-stats">
        <div className="stat-item-program">
          <span className="stat-value-program">{stats.lectures}</span> {t("course_events")}
        </div>
        
        <div className="stat-item-program">
          <span className="stat-value-program">{stats.hours}</span> {t("course_hours")}
        </div>
        <div className="stat-item-program">
          <span className="stat-value-program"> </span> {t("course_language")}
        </div>
        <div className="stat-item-program certificate-stat">
          {t("course_certificate")}
          <div className="tooltip-container">
            <img src={infoIcon} alt="Info" className="info-icon" />
            <div className="tooltip-text">
              {t("course_certificate_description")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseTimelineOverview;
